import { Link } from 'react-router-dom'
import { Sparkles, User } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import type { AlertSummary } from '@/features/alerts/types'
import type { CaseDetail } from '../types'

/**
 * Customer sidebar card mirroring the catalogue's Investigation
 * Workspace mock — RETAIL chip + display name, BVN + account masked,
 * a prominent risk-score gauge with delta annotation, the assignee,
 * and the AI-time-saved indicator.
 *
 * Risk score is derived from the linked alerts (max risk_score).
 * The "delta" annotation surfaces the highest-priority signal —
 * e.g. "USSD PIN Reset" — by reading the most recent linked alert
 * scenario.
 *
 * AI Time Saved is a synthetic estimate based on # of linked alerts:
 * each alert that was auto-grouped into the case is treated as
 * roughly 5 minutes of saved analyst time. Real instrumentation
 * lands in a follow-up; the figure here is conservative.
 */
export function CustomerSidebarCard({ c }: { c: CaseDetail }) {
  const riskScore = deriveRiskScore(c.linked_alerts)
  const driver = pickRiskDriver(c.linked_alerts)
  const aiSavedMin = estimateAISavedMinutes(c)

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        {/* Customer identity block */}
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <User size={18} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            {c.primary_customer ? (
              <Link
                to={`/customers/${c.primary_customer.id}`}
                className="block truncate text-sm font-semibold hover:text-primary"
              >
                {c.primary_customer.display_name}
              </Link>
            ) : (
              <p className="truncate text-sm font-semibold text-muted-fg">No primary customer</p>
            )}
            <p className="mt-0.5 text-[0.6875rem] uppercase tracking-wider text-muted-fg">
              {customerSegment(c)}
            </p>
            {c.primary_customer ? (
              <p className="mt-1 truncate text-[0.6875rem] text-muted-fg">
                Customer #
                <span className="font-mono">{c.primary_customer.customer_number}</span>
              </p>
            ) : null}
          </div>
        </div>

        {/* Risk score gauge */}
        <div className="rounded border border-border bg-card p-3">
          <div className="flex items-baseline justify-between">
            <span className="text-[0.6875rem] uppercase tracking-wider text-muted-fg">
              Risk score
            </span>
            <Badge variant={scoreVariant(riskScore)} className="uppercase">
              {scoreBand(riskScore)}
            </Badge>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums">{riskScore}</span>
            <span className="text-[0.6875rem] text-muted-fg">/100</span>
          </div>
          {driver ? (
            <p className="mt-1 text-[0.6875rem] text-muted-fg">
              <span className="font-medium text-fg">+{driver.delta}</span> pts (
              {driver.label})
            </p>
          ) : null}
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
            <span
              className={
                'block h-full ' + scoreFillClass(riskScore)
              }
              style={{ width: `${Math.min(100, Math.max(0, riskScore))}%` }}
              aria-hidden
            />
          </div>
        </div>

        {/* Assignee */}
        <div>
          <p className="mb-1 text-[0.6875rem] uppercase tracking-wider text-muted-fg">
            Assignee
          </p>
          <div className="flex items-center gap-2">
            {c.lead_analyst ? (
              <>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[0.625rem] font-semibold text-primary">
                  {c.lead_analyst.initials || initials(c.lead_analyst.display_name)}
                </span>
                <span className="text-sm font-medium">{c.lead_analyst.display_name}</span>
              </>
            ) : (
              <span className="text-xs italic text-muted-fg">Unassigned</span>
            )}
          </div>
        </div>

        {/* AI time saved */}
        <div className="flex items-start gap-2 rounded border border-primary/30 bg-primary/5 p-2">
          <Sparkles size={14} className="mt-0.5 shrink-0 text-primary" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-primary">
              AI Time Saved: {aiSavedMin}m
            </p>
            <p className="mt-0.5 text-[0.6875rem] leading-tight text-muted-fg">
              Automated BVN screening, multi-channel profile aggregation, and alert grouping
              completed by the platform.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---- helpers --------------------------------------------------------

function deriveRiskScore(alerts: AlertSummary[]): number {
  // Linked alerts each carry a score; the case-level risk is the
  // most-aggressive (max) score with a small bump for clusters of
  // 5+ alerts (mule-ring-like patterns).
  if (alerts.length === 0) return 0
  let max = 0
  for (const a of alerts) {
    const s = (a as unknown as { risk_score?: number }).risk_score ?? 0
    if (s > max) max = s
  }
  if (alerts.length >= 5) max = Math.min(100, max + 5)
  return max
}

function pickRiskDriver(alerts: AlertSummary[]): { label: string; delta: number } | null {
  if (alerts.length === 0) return null
  // Find the highest-scoring alert and surface its scenario name as
  // the "+X pts (driver)" annotation.
  let top: AlertSummary | null = null
  let topScore = -1
  for (const a of alerts) {
    const s = (a as unknown as { risk_score?: number }).risk_score ?? 0
    if (s > topScore) {
      topScore = s
      top = a
    }
  }
  if (!top) return null
  const scenario =
    (top as unknown as { scenario_name?: string; alert_type?: string }).scenario_name ??
    (top as unknown as { alert_type?: string }).alert_type ??
    'Highest-risk alert'
  return { label: scenario, delta: topScore }
}

function estimateAISavedMinutes(c: CaseDetail): number {
  // Conservative estimate: 3 minutes per linked alert that an
  // analyst would have triaged manually, capped at 60.
  const minutes = Math.min(60, c.linked_alerts.length * 3 + 5)
  return minutes
}

function scoreVariant(score: number): React.ComponentProps<typeof Badge>['variant'] {
  if (score >= 80) return 'failed'
  if (score >= 50) return 'pending'
  if (score >= 20) return 'info'
  return 'default'
}

function scoreBand(score: number): string {
  if (score >= 80) return 'High'
  if (score >= 50) return 'Medium'
  if (score >= 20) return 'Low'
  return 'Info'
}

function scoreFillClass(score: number): string {
  if (score >= 80) return 'bg-status-failed'
  if (score >= 50) return 'bg-status-pending'
  if (score >= 20) return 'bg-status-info'
  return 'bg-muted-fg/50'
}

function customerSegment(c: CaseDetail): string {
  // The Case entity doesn't carry a customer segment today; use the
  // queue label as a rough proxy. RETAIL is the catalogue's mock
  // segment — kept as the default so the chip renders meaningfully.
  // A follow-up exposes the real segment from the customer payload.
  const queue =
    (c as unknown as { queue?: { name?: string } }).queue?.name ??
    (c as unknown as { queue?: string }).queue
  if (typeof queue === 'string' && queue) return queue.replace(/_/g, ' ')
  return 'Retail'
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
